import { Card } from "@/components/ui/card";
import { toTitleCase } from "@/utils/string";

import { ReactComponent as CheckIcon } from "@assets/svg/check-blue.svg";

import { Checkbox } from "@components/ui/checkbox";
import { Input } from "@components/ui/input";

type Section = {
  id: string;
  name: string;
  order: number;
};

type AvailableModulesProps = {
  unselectedSections: Section[];
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  selectAllChecked: boolean;
  checkedSections: Set<string>;
  onSelectAll: (checked: boolean) => void;
  onCheckSection: (sectionId: string, checked: boolean) => void;
  onAddSelected: () => void;
};

export const AvailableModules = ({
  unselectedSections,
  searchQuery,
  setSearchQuery,
  selectAllChecked,
  checkedSections,
  onSelectAll,
  onCheckSection,
  onAddSelected,
}: AvailableModulesProps) => {
  return (
    <Card className="p-4 pb-0">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">Available Modules</h3>
        <button
          type="button"
          className="rounded-md border border-border bg-background-white px-3 py-1.5 text-sm font-medium hover:bg-background-muted disabled:cursor-not-allowed disabled:opacity-50"
          onClick={onAddSelected}
          disabled={checkedSections.size === 0}
        >
          Add Selected ({checkedSections.size})
        </button>
      </div>

      <div className="flex gap-4">
        {/* Select All */}
        <div className="flex items-center space-x-2">
          <Checkbox
            id="select-all"
            checked={selectAllChecked}
            onCheckedChange={onSelectAll}
          />
          <label
            htmlFor="select-all"
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            Select All
          </label>
        </div>

        {/* Search */}
        <div className="flex-1">
          <Input
            placeholder="Search module here..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-8"
          />
        </div>
      </div>

      {/* Available Modules List */}
      <div className="max-h-96 space-y-2 overflow-y-auto pb-2">
        {unselectedSections.length === 0 && (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <CheckIcon className="mb-2 h-12 w-12 text-gray-300" />
            <p className="text-sm text-gray-500">No Modules Available</p>
            <p className="text-xs text-gray-400">
              All available modules are already selected.
            </p>
          </div>
        )}
        {unselectedSections.map((section) => (
          <div
            key={section.id}
            className="flex items-center space-x-2 rounded-lg border border-gray-200 bg-blue-50 p-3"
          >
            <Checkbox
              checked={checkedSections.has(section.id)}
              onCheckedChange={(checked) =>
                onCheckSection(section.id, checked as boolean)
              }
            />
            <label className="flex-1 text-sm font-medium">
              {toTitleCase(section.name)}
            </label>
          </div>
        ))}
      </div>
    </Card>
  );
};
